import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import MainLayout from '../../components/MainLayout';
import {
  fetchExpoEquipments as fetchEquipments,
  fetchExpoBoardSnapshot,
  fetchExpoPiHistory,
  clearExpoPiHistory,
} from '../../services/api';
import { useAlarmNotification } from '../../context/AlarmNotificationContext';
import './EquipmentBoard.css';

const SPLIT_OPTIONS = [
  { value: 1, icon: 'crop_square', label: '1분할' },
  { value: 2, icon: 'splitscreen', label: '2분할' },
  { value: 4, icon: 'grid_view', label: '4분할' },
];

const SHOT_COLUMNS = [
  ['shotTime', '시간'],
  ['craftId', '금형ID'],
  ['shotCount', '샷번호'],
  ['cycleTime', '사이클타임'],
  ['injTime', '사출시간'],
  ['holdTime', '보압시간'],
  ['chargeTime', '계량시간'],
  ['moldOpenTime', '형개시간'],
  ['moldCloseTime', '형폐시간'],
  ['fetchTime', '취출시간'],
  ['ejectTime', '이젝트'],
];

function EquipmentPanel({ index, machines, splitCount, snapshot, onNewAlarm, onSelectedIdChange, panelAlarms, onRemovePanelAlarm }) {
  const [selectedId, setSelectedId] = useState(() => {
    // localStorage에서 저장된 선택 복원
    try {
      const saved = JSON.parse(localStorage.getItem('eq-board-panels') || '{}');
      return saved[index] || null;
    } catch { return null; }
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [alarmData, setAlarmData] = useState(null);
  const [shotRows, setShotRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newShotIds, setNewShotIds] = useState(new Set());
  const prevMaxShotRef = useRef(null);
  const prevAlarmCountRef = useRef(null);
  const dropdownRef = useRef(null);

  // 첫 로드 시 저장된 값 없으면 machines[index] 자동 선택
  useEffect(() => {
    if (machines.length > 0 && !selectedId) {
      const idx = Math.min(index, machines.length - 1);
      setSelectedId(machines[idx]?.id || null);
    }
  }, [machines]);

  // 설비 변경 시 리셋 + 부모에 알림
  useEffect(() => {
    prevMaxShotRef.current = null;
    prevAlarmCountRef.current = null;
    isFirstLoadRef.current = true;
    setNewShotIds(new Set());
    if (onSelectedIdChange) onSelectedIdChange(index, selectedId);
  }, [selectedId]);

  const isFirstLoadRef = useRef(true);

  // 부모가 0.5초마다 batch 로 가져온 snapshot 을 props 로 받아 화면에 반영
  useEffect(() => {
    if (!selectedId || !snapshot) {
      if (isFirstLoadRef.current) setLoading(true);
      return;
    }
    // 부모 ref 갱신 직후 0.5초 동안 stale snapshot 이 들어올 수 있음 → 머신 ID 일치 검증
    if (snapshot.id !== selectedId) return;
    // 알람 처리 — 신규 알람 감지 (active 만 팝업)
    const newAlarms = snapshot.alarm?.alarmHistory || [];
    if (prevAlarmCountRef.current !== null && newAlarms.length > prevAlarmCountRef.current) {
      const addedCount = newAlarms.length - prevAlarmCountRef.current;
      const addedAlarms = newAlarms.slice(0, addedCount);
      const machine = machines.find(m => m.id === selectedId);
      onNewAlarm(addedAlarms, machine?.equipmentName || `설비 #${selectedId}`);
    }
    prevAlarmCountRef.current = newAlarms.length;
    setAlarmData(snapshot.alarm);

    // 샷 데이터 처리 + 새 샷 감지 (5초간 하이라이트)
    // 각 샷이 독립된 5초 타이머를 가져, 빠른 연속 사출에서도 모든 샷이 정확히 5초씩 깜빡임.
    const rows = snapshot.shots?.rows || [];
    if (rows.length > 0) {
      const maxShot = Math.max(...rows.map(r => r.shotCount || 0));
      if (prevMaxShotRef.current !== null && maxShot > prevMaxShotRef.current) {
        const prevMax = prevMaxShotRef.current;
        const newShots = rows
          .map(r => r.shotCount)
          .filter(sc => sc != null && sc > prevMax);

        // 기존 newShotIds 에 누적 (이전 샷 깜빡임 덮어쓰지 않음)
        setNewShotIds(prev => {
          const next = new Set(prev);
          newShots.forEach(sc => next.add(sc));
          return next;
        });

        // 각 샷별 독립 5초 타이머
        newShots.forEach(sc => {
          setTimeout(() => {
            setNewShotIds(curr => {
              if (!curr.has(sc)) return curr;
              const updated = new Set(curr);
              updated.delete(sc);
              return updated;
            });
          }, 5000);
        });
      }
      prevMaxShotRef.current = maxShot;
    }
    setShotRows(rows);

    setLoading(false);
    isFirstLoadRef.current = false;
  }, [snapshot, selectedId, machines, onNewAlarm]);

  // 드롭다운 외부 클릭
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const selectedMachine = machines.find(m => m.id === selectedId);
  const alarms = alarmData?.alarmHistory || [];

  const handleSelect = (id) => {
    setSelectedId(id);
    setIsDropdownOpen(false);
  };

  const fmtVal = (v) => {
    if (v == null) return '-';
    if (typeof v === 'number') return v.toLocaleString();
    return String(v);
  };

  const fmtTime = (t) => {
    if (!t) return '-';
    const idx = t.indexOf('T');
    if (idx < 0) return t;
    const date = t.substring(5, 10).replace('-', '/');
    const time = t.substring(idx + 1, idx + 9);
    return `${date} ${time}`;
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171', icon: 'error' };
      case 'WARNING':
        return { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24', icon: 'warning' };
      case 'INFO':
        return { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.3)', text: '#60a5fa', icon: 'info' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', border: 'rgba(107, 114, 128, 0.3)', text: '#9ca3af', icon: 'circle' };
    }
  };

  return (
    <div className="eq-panel">
      <div className="eq-panel-header">
        <div className="eq-panel-selector" ref={dropdownRef}>
          <button className="eq-panel-selector-btn" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span className="material-symbols-outlined eq-panel-icon">precision_manufacturing</span>
            <span className="eq-panel-name">{selectedMachine?.equipmentName || '설비 선택'}</span>
            {selectedMachine && (
              <span className="eq-panel-model">{selectedMachine.manufacturer} · {selectedMachine.model}</span>
            )}
            <span className="material-symbols-outlined eq-panel-arrow">
              {isDropdownOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {isDropdownOpen && (
            <div className="eq-panel-dropdown">
              {machines.map(m => (
                <div
                  key={m.id}
                  className={`eq-panel-dropdown-item ${selectedId === m.id ? 'active' : ''}`}
                  onClick={() => handleSelect(m.id)}
                >
                  <span className="eq-panel-dropdown-name">{m.equipmentName}</span>
                  <span className="eq-panel-dropdown-model">{m.manufacturer} · {m.model}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {loading && <span className="eq-panel-loading">조회 중...</span>}
        {selectedId && (
          <button
            className="eq-history-btn"
            onClick={() => setIsHistoryOpen(true)}
            title="라즈베리파이 이벤트 이력"
          >
            <span className="material-symbols-outlined">history</span>
          </button>
        )}
      </div>

      <div className="eq-panel-body">
        {!selectedId ? (
          <div className="eq-panel-empty">설비를 선택하세요</div>
        ) : (
          <div className="eq-panel-content">
            {/* 알람 이력 */}
            <div className="eq-alarm-section">
              <div className="eq-section-title">
                <span className="material-symbols-outlined">notifications</span>
                알람 이력
                {alarms.length > 0 && <span className="eq-alarm-count">{alarms.length}</span>}
              </div>
              <div className="eq-alarm-list">
                {alarms.length === 0 ? (
                  <div className="eq-alarm-empty">알람 없음</div>
                ) : (
                  alarms.map((a, i) => {
                    const ss = getSeverityStyle(a.severity);
                    return (
                      <div
                        key={i}
                        className="eq-alarm-card"
                        style={{ backgroundColor: ss.bg, borderColor: ss.border }}
                      >
                        <div className="eq-alarm-card-row1">
                          <span className="material-symbols-outlined" style={{ fontSize: 16, color: ss.text }}>{ss.icon}</span>
                          <span className="eq-alarm-code">#{a.code}</span>
                          <span className="eq-alarm-value">{a.value || ''}</span>
                          {a.status === 'active' && (
                            <span className="eq-alarm-live">
                              <span className="eq-alarm-live-dot" />LIVE
                            </span>
                          )}
                        </div>
                        <div className="eq-alarm-msg" style={{ color: ss.text }}>{a.message}</div>
                        <div className="eq-alarm-footer">
                          <span className="eq-alarm-time">발생: {fmtTime(a.time)}</span>
                          <span className="eq-alarm-status" style={{ color: a.status === 'active' ? '#f87171' : '#4ade80' }}>
                            {a.status === 'active' ? '발생중' : '해결됨'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 샷 데이터 테이블 */}
            <div className="eq-shot-section">
              <div className="eq-section-title">
                <span className="material-symbols-outlined">table_chart</span>
                샷 데이터
                <span className="eq-shot-count">{shotRows.length}건</span>
              </div>
              <div className="eq-shot-table-wrap">
                <table className="eq-shot-table">
                  <thead>
                    <tr>
                      {SHOT_COLUMNS.map(([key, label]) => (
                        <th key={key}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shotRows.length === 0 ? (
                      <tr><td colSpan={SHOT_COLUMNS.length} className="eq-shot-empty">데이터 없음</td></tr>
                    ) : (
                      shotRows.map((row, idx) => (
                        <tr
                          key={row.shotCount || idx}
                          className={newShotIds.has(row.shotCount) ? 'eq-new-shot' : ''}
                        >
                          {SHOT_COLUMNS.map(([key]) => (
                            <td key={key}>{key === 'shotTime' ? fmtTime(row[key]) : fmtVal(row[key])}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 패널 내부 알람 오버레이 */}
      {panelAlarms && panelAlarms.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '56px',
          right: '8px',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          maxHeight: 'calc(100% - 64px)',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          <style>{`
            @keyframes panelAlarmSlide {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
            @keyframes panelAlarmPulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
          {panelAlarms.map((alarm) => (
            <div key={alarm.id} style={{
              background: 'linear-gradient(135deg, rgba(26,26,46,0.95) 0%, rgba(22,33,62,0.95) 100%)',
              border: `2px solid ${alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B'}`,
              borderRadius: '4px',
              padding: '10px 14px',
              minWidth: splitCount === 1 ? '320px' : '240px',
              maxWidth: splitCount === 1 ? '360px' : '280px',
              boxShadow: `0 4px 16px ${alarm.severity === 'CRITICAL' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
              animation: 'panelAlarmSlide 0.3s ease-out',
              fontSize: splitCount >= 4 ? '11px' : '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '14px', marginRight: '6px', animation: 'panelAlarmPulse 1s infinite' }}>🚨</span>
                <span style={{ color: alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B', fontWeight: 'bold', fontSize: splitCount >= 4 ? '11px' : '13px' }}>알람 발생</span>
                <button
                  onClick={() => onRemovePanelAlarm && onRemovePanelAlarm(alarm.id)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px' }}
                >✕</button>
              </div>
              {alarm.equipmentName && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 6px',
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '3px',
                  marginBottom: '6px',
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: splitCount >= 4 ? '12px' : '14px', color: '#9ca3af' }}>
                    precision_manufacturing
                  </span>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: splitCount >= 4 ? '11px' : '12px' }}>
                    {alarm.equipmentName}
                  </span>
                  {alarm.factoryName && (
                    <span style={{ color: '#888', fontSize: splitCount >= 4 ? '10px' : '11px' }}>
                      · {alarm.factoryName}
                    </span>
                  )}
                </div>
              )}
              <div style={{ color: '#F59E0B', fontWeight: 'bold', marginBottom: '4px' }}>{alarm.code}</div>
              <div style={{ color: '#aaa', fontSize: splitCount >= 4 ? '10px' : '11px' }}>{alarm.message || alarm.desc}</div>
              <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>{alarm.time}</div>
            </div>
          ))}
        </div>
      )}

      {/* Pi 이벤트 이력 모달 */}
      {isHistoryOpen && selectedId && (
        <PiHistoryModal
          machineId={selectedId}
          machineName={selectedMachine?.equipmentName || `설비 #${selectedId}`}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}
    </div>
  );
}

// Pi 원본 이벤트 이력 모달 (overlay)
function PiHistoryModal({ machineId, machineName, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const loadHistory = useCallback(() => {
    setLoading(true);
    fetchExpoPiHistory(machineId, 200)
      .then(data => setEvents(data?.events || []))
      .catch(err => { console.error(err); setEvents([]); })
      .finally(() => setLoading(false));
  }, [machineId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleClear = async () => {
    if (clearing) return;
    const ok = window.confirm(
      `${machineName}의 모든 이력을 초기화합니다.\n\n` +
      `· Pi 이벤트 이력\n` +
      `· 알람 이력\n` +
      `· 샷 데이터\n` +
      `· 사출 누적 카운트\n\n` +
      `복구는 불가능합니다. 진행할까요?`
    );
    if (!ok) return;
    setClearing(true);
    try {
      await clearExpoPiHistory(machineId);
      setEvents([]);
    } catch (err) {
      alert('이력 초기화에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setClearing(false);
    }
  };

  const fmtTime = (t) => {
    if (!t) return '-';
    const idx = t.indexOf('T');
    if (idx < 0) return t;
    const date = t.substring(5, 10).replace('-', '/');
    const time = t.substring(idx + 1, idx + 9);
    return `${date} ${time}`;
  };

  const renderEventLabel = (e) => {
    switch ((e.eventType || '').toUpperCase()) {
      case 'DOOR_OPEN':  return { icon: 'door_open',     color: '#f87171', label: `안전문 #${e.doorNumber} 열림` };
      case 'DOOR_CLOSE': return { icon: 'door_front',    color: '#4ade80', label: `안전문 #${e.doorNumber} 닫힘` };
      case 'INJECT':     return { icon: 'precision_manufacturing', color: '#60a5fa', label: `사출 #${e.count ?? '-'}` };
      case 'BOOT':       return { icon: 'power_settings_new', color: '#a78bfa', label: '게이트웨이 부팅' };
      case 'HEARTBEAT':  return { icon: 'monitor_heart', color: '#9ca3af', label: '하트비트' };
      default:           return { icon: 'circle', color: '#9ca3af', label: e.eventType || '?' };
    }
  };

  return ReactDOM.createPortal(
    <div className="eq-history-overlay" onClick={onClose}>
      <div className="eq-history-modal" onClick={e => e.stopPropagation()}>
        <div className="eq-history-header">
          <div>
            <div className="eq-history-title">
              <span className="material-symbols-outlined">history</span>
              {machineName} — Pi 이벤트 이력
            </div>
            <div className="eq-history-subtitle">최신순 / 최대 200건</div>
          </div>
          <div className="eq-history-actions">
            <button
              className="eq-history-refresh"
              onClick={loadHistory}
              title="새로고침"
              disabled={loading || clearing}
            >
              <span className="material-symbols-outlined">refresh</span>
            </button>
            <button
              className="eq-history-clear"
              onClick={handleClear}
              title="이력 초기화"
              disabled={loading || clearing || events.length === 0}
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                color: '#f87171',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: (loading || clearing || events.length === 0) ? 'not-allowed' : 'pointer',
                opacity: (loading || clearing || events.length === 0) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete_sweep</span>
              <span>{clearing ? '초기화 중...' : '초기화'}</span>
            </button>
            <button className="eq-history-close" onClick={onClose} title="닫기">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        <div className="eq-history-body">
          {loading ? (
            <div className="eq-history-empty">로딩 중...</div>
          ) : events.length === 0 ? (
            <div className="eq-history-empty">수신된 이벤트가 없습니다.</div>
          ) : (
            <div className="eq-history-list">
              {events.map((e, i) => {
                const meta = renderEventLabel(e);
                return (
                  <div key={i} className="eq-history-item">
                    <span className="material-symbols-outlined eq-history-icon" style={{ color: meta.color }}>{meta.icon}</span>
                    <div className="eq-history-event-col">
                      <div className="eq-history-event-label" style={{ color: meta.color }}>{meta.label}</div>
                      <div className="eq-history-event-type">{e.eventType}</div>
                    </div>
                    <div className="eq-history-time">{fmtTime(e.time)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function EquipmentBoard() {
  const [splitCount, setSplitCount] = useState(() => {
    const saved = localStorage.getItem('eq-board-split');
    return saved ? parseInt(saved) : 1;
  });
  const [machines, setMachines] = useState([]);
  const [alarmPopup, setAlarmPopup] = useState(null);
  const [panelAlarms, setPanelAlarms] = useState({});
  // 통합 스냅샷 — 전시 데모용 0.5초 batch 폴링 결과
  const [snapshotByMachineId, setSnapshotByMachineId] = useState({});
  const panelSelectedIdsRef = useRef({});
  const { alarms: contextAlarms, removeAlarm: removeContextAlarm, updateEquipmentBoardIds } = useAlarmNotification();

  useEffect(() => {
    fetchEquipments()
      .then(data => { if (Array.isArray(data)) setMachines(data); })
      .catch(err => console.error('장비 목록 조회 실패:', err));
  }, []);

  // 전시 데모: 0.5초마다 통합 스냅샷 폴링 (모든 설비 한 번에)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const snap = await fetchExpoBoardSnapshot(50);
        if (cancelled) return;
        const map = {};
        (snap?.machines || []).forEach(m => { map[m.id] = m; });
        console.log(`[poll] snapshot ${new Date().toLocaleTimeString('ko-KR', { hour12: false })}.${String(Date.now() % 1000).padStart(3, '0')} machines=${Object.keys(map).length}`);
        setSnapshotByMachineId(map);
      } catch (e) {
        if (!cancelled) console.error('snapshot 폴링 실패:', e);
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleSplitChange = useCallback((count) => {
    setSplitCount(count);
    localStorage.setItem('eq-board-split', String(count));
  }, []);

  const handlePanelSelectedIdChange = useCallback((panelIndex, equipmentId) => {
    panelSelectedIdsRef.current[panelIndex] = equipmentId;
    const ids = Object.values(panelSelectedIdsRef.current).filter(Boolean);
    updateEquipmentBoardIds(ids);
    // 패널 선택 저장
    localStorage.setItem('eq-board-panels', JSON.stringify(panelSelectedIdsRef.current));
  }, [updateEquipmentBoardIds]);

  // 실제 알람(AlarmNotificationContext)을 패널별로 분배
  const prevContextAlarmsRef = useRef([]);
  useEffect(() => {
    if (contextAlarms.length > prevContextAlarmsRef.current.length) {
      const newOnes = contextAlarms.slice(0, contextAlarms.length - prevContextAlarmsRef.current.length);
      for (const alarm of newOnes) {
        // alarm.equipmentId로 어느 패널에 해당하는지 찾기
        const panelIndex = Object.entries(panelSelectedIdsRef.current)
          .find(([, eqId]) => eqId === alarm.equipmentId)?.[0];
        if (panelIndex !== undefined) {
          setPanelAlarms(prev => ({
            ...prev,
            [panelIndex]: [alarm, ...(prev[panelIndex] || [])],
          }));
        }
      }
    }
    prevContextAlarmsRef.current = contextAlarms;
  }, [contextAlarms]);

  const addPanelAlarm = useCallback((panelIndex, alarm) => {
    setPanelAlarms(prev => ({
      ...prev,
      [panelIndex]: [alarm, ...(prev[panelIndex] || [])],
    }));
  }, []);

  const removePanelAlarm = useCallback((panelIndex, alarmId) => {
    setPanelAlarms(prev => ({
      ...prev,
      [panelIndex]: (prev[panelIndex] || []).filter(a => a.id !== alarmId),
    }));
    removeContextAlarm(alarmId);
  }, [removeContextAlarm]);

  const handleNewAlarm = useCallback((newAlarms, equipmentName) => {
    // active 상태인 알람만 팝업
    const activeAlarms = newAlarms.filter(a => a.status === 'active');
    if (activeAlarms.length > 0) {
      setAlarmPopup({ alarms: activeAlarms, equipmentName, currentIndex: 0 });
    }
  }, []);

  const dismissAlarmPopup = () => {
    if (!alarmPopup) return;
    const next = alarmPopup.currentIndex + 1;
    if (next < alarmPopup.alarms.length) {
      setAlarmPopup({ ...alarmPopup, currentIndex: next });
    } else {
      setAlarmPopup(null);
    }
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171', icon: 'error' };
      case 'WARNING':
        return { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24', icon: 'warning' };
      case 'INFO':
        return { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.3)', text: '#60a5fa', icon: 'info' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', border: 'rgba(107, 114, 128, 0.3)', text: '#9ca3af', icon: 'circle' };
    }
  };

  const fmtTime = (t) => {
    if (!t) return '-';
    const idx = t.indexOf('T');
    if (idx < 0) return t;
    const date = t.substring(5, 10).replace('-', '/');
    const time = t.substring(idx + 1, idx + 9);
    return `${date} ${time}`;
  };

  return (
    <MainLayout>
      <div className="eq-board-container">
        <div className={`eq-board-grid split-${splitCount}`}>
          {Array.from({ length: splitCount }, (_, i) => {
            const panelSelectedId = panelSelectedIdsRef.current[i];
            return (
              <EquipmentPanel
                key={i}
                index={i}
                machines={machines}
                splitCount={splitCount}
                snapshot={panelSelectedId ? snapshotByMachineId[panelSelectedId] : null}
                onNewAlarm={handleNewAlarm}
                onSelectedIdChange={handlePanelSelectedIdChange}
                panelAlarms={panelAlarms[i] || []}
                onRemovePanelAlarm={(alarmId) => removePanelAlarm(i, alarmId)}
              />
            );
          })}
        </div>

        <div className="eq-fab-toolbar">
          {SPLIT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`eq-fab-btn ${splitCount === opt.value ? 'active' : ''}`}
              onClick={() => handleSplitChange(opt.value)}
              title={opt.label}
            >
              <span className="material-symbols-outlined">{opt.icon}</span>
            </button>
          ))}
        </div>

        {/* 새 알람 팝업 - 알람 카드 형태로 크게 표시 */}
        {alarmPopup && (() => {
          const a = alarmPopup.alarms[alarmPopup.currentIndex];
          const ss = getSeverityStyle(a.severity);
          const remaining = alarmPopup.alarms.length - alarmPopup.currentIndex - 1;
          return (
            <div className="eq-alarm-popup-overlay" onClick={dismissAlarmPopup}>
              <div className="eq-alarm-popup" onClick={e => e.stopPropagation()} style={{ '--alarm-bg': ss.bg, borderColor: ss.border }}>
                {alarmPopup.equipmentName && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#9ca3af' }}>
                      precision_manufacturing
                    </span>
                    <span style={{ color: '#fff', fontWeight: '700', fontSize: '15px' }}>
                      {alarmPopup.equipmentName}
                    </span>
                  </div>
                )}
                <div className="eq-alarm-popup-row1">
                  <span className="material-symbols-outlined eq-alarm-popup-icon" style={{ color: ss.text }}>{ss.icon}</span>
                  <span className="eq-alarm-popup-code" style={{ color: '#fff' }}>#{a.code}</span>
                  <span className="eq-alarm-popup-dash">-</span>
                  <span className="eq-alarm-popup-msg">{a.message}</span>
                  {remaining > 0 && <span className="eq-alarm-popup-remaining">+{remaining}건</span>}
                </div>
                <div className="eq-alarm-popup-row2">
                  <span className="eq-alarm-popup-time">발생: {fmtTime(a.time)}</span>
                  <span className="eq-alarm-popup-status" style={{ color: a.status === 'active' ? '#f87171' : '#4ade80' }}>
                    {a.status === 'active' ? '발생중' : '해결됨'}
                  </span>
                </div>
                <button className="eq-alarm-popup-confirm" onClick={dismissAlarmPopup}>
                  {remaining > 0 ? `확인 (다음 ${remaining}건)` : '확인'}
                </button>
              </div>
            </div>
          );
        })()}
      </div>
      {/* <AlarmTestButton splitCount={splitCount} addPanelAlarm={addPanelAlarm} panelSelectedIds={panelSelectedIdsRef} machines={machines} /> */}
    </MainLayout>
  );
}

/* 알람 테스트 컴포넌트 - 패널별 알람 발생 */
function AlarmTestButton({ splitCount, addPanelAlarm, panelSelectedIds, machines }) {
  const callCountRef = useRef(0);

  const testAlarms = [
    { code: 'ERR_07', severity: 'WARNING', desc: '사출 쿠션 에러 (INJCUSHERR)' },
    { code: 'ERR_0A', severity: 'CRITICAL', desc: '사이클 타임 초과 (CYCTIMEXCEED)' },
    { code: 'ERR_01', severity: 'WARNING', desc: '호퍼 비어있음 (HOPPEREMPTY)' },
  ];

  const playAlarmSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 2.2, 4.4].forEach((cycleStart) => {
        [0, 0.4, 0.8].forEach((beep) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'square';
          osc.frequency.value = 880;
          gain.gain.value = 0.3;
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(ctx.currentTime + cycleStart + beep);
          osc.stop(ctx.currentTime + cycleStart + beep + 0.15);
        });
      });
    } catch (e) {
      console.error('알람 소리 재생 실패:', e);
    }
  };

  const triggerAlarm = () => {
    playAlarmSound();
    const panelIndex = callCountRef.current % splitCount;
    const testData = testAlarms[callCountRef.current % testAlarms.length];
    const selectedId = panelSelectedIds.current[panelIndex];
    const machine = machines.find(m => m.id === selectedId);

    const alarm = {
      id: Date.now(),
      time: new Date().toLocaleString('ko-KR'),
      equipment: machine?.equipmentName || `설비 #${selectedId || '?'}`,
      ...testData,
    };
    addPanelAlarm(panelIndex, alarm);
    callCountRef.current += 1;
  };

  return ReactDOM.createPortal(
    <button
      onClick={triggerAlarm}
      style={{
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        background: '#EF4444',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        fontSize: '24px',
        boxShadow: '0 4px 12px rgba(239,68,68,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s',
      }}
      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
      title="알람 테스트 (패널별)"
    >
      🔔
    </button>,
    document.body
  );
}

export default EquipmentBoard;
